import React from 'react';
import { BlockShape } from './ShapeDefinitions';

// Define CELL_SIZE constant directly (same value as in DrawingInterface.tsx)
const CELL_SIZE = 30; 

export interface PlacedShapeData {
  id: string;
  shape: BlockShape;
  row: number;
  col: number;
  rotation: 0 | 90 | 180 | 270; // Rotation in degrees
}

interface PlacedShapeProps {
  data: PlacedShapeData;
  onClick: (id: string) => void;
  isSelected: boolean;
  onDelete?: (id: string) => void;
  onRotate?: (id: string) => void;
}

const PlacedShape: React.FC<PlacedShapeProps> = ({ 
  data, 
  onClick, 
  isSelected, 
  onDelete, 
  onRotate 
}) => {
  const { shape, row, col, rotation, id } = data;

  // Calculate dimensions
  const cellSize = CELL_SIZE;
  
  // Ensure we have a valid color
  const color = shape.color || '#ff0000';
  
  // Apply rotation to matrix
  const getRotatedMatrix = () => {
    const { matrix } = shape;
    const rows = matrix.length;
    const cols = matrix[0].length;
    
    if (rotation === 0) return matrix;
    
    let rotatedMatrix = matrix;
    
    if (rotation === 90) {
      rotatedMatrix = Array(cols).fill(null).map(() => Array(rows).fill(false));
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          rotatedMatrix[c][rows - 1 - r] = matrix[r][c];
        }
      }
    } else if (rotation === 180) {
      rotatedMatrix = Array(rows).fill(null).map(() => Array(cols).fill(false));
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          rotatedMatrix[rows - 1 - r][cols - 1 - c] = matrix[r][c];
        }
      }
    } else if (rotation === 270) {
      rotatedMatrix = Array(cols).fill(null).map(() => Array(rows).fill(false));
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          rotatedMatrix[cols - 1 - c][r] = matrix[r][c];
        }
      }
    }
    
    return rotatedMatrix;
  };
  
  const rotatedMatrix = getRotatedMatrix();
  const rotatedWidth = rotatedMatrix[0].length;
  const rotatedHeight = rotatedMatrix.length;

  return (
    <div 
      className={`placed-shape ${isSelected ? 'selected' : ''}`}
      style={{
        position: 'absolute',
        top: `${row * cellSize}px`,
        left: `${col * cellSize}px`,
        width: `${rotatedWidth * cellSize}px`,
        height: `${rotatedHeight * cellSize}px`,
        zIndex: isSelected ? 20 : 10,
        pointerEvents: 'auto',
      }}
      onClick={() => onClick(id)}
    >
      {/* Render individual cells based on matrix values */}
      {rotatedMatrix.map((matrixRow, rowIndex) => 
        matrixRow.map((cell, colIndex) => 
          cell && (
            <div
              key={`${rowIndex}-${colIndex}`}
              className="shape-cell"
              style={{
                position: 'absolute',
                top: `${rowIndex * cellSize}px`,
                left: `${colIndex * cellSize}px`,
                width: `${cellSize}px`,
                height: `${cellSize}px`,
                backgroundColor: color,
                zIndex: isSelected ? 20 : 10,
                borderRadius: '0',
              }}
            />
          )
        )
      )}
      
      {isSelected && (
        <div className="shape-controls">
          {onDelete && (
            <button 
              className="delete-button" 
              onClick={(e) => {
                e.stopPropagation();
                onDelete(id);
              }}
            >
              ×
            </button>
          )}
          
          {onRotate && (
            <button 
              className="rotate-button" 
              onClick={(e) => {
                e.stopPropagation();
                onRotate(id);
              }}
            >
              ↻
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PlacedShape; 